import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EmailIcon from '@mui/icons-material/Email';
import {
    Avatar,
    Box,
    Button,
    Slide,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { addAlphaToColor } from 'shared/utils/colors';

import { UserProfileIcon } from '@/assets/svg';
import { Modal } from '@/components';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';
import { formatUnixTimestamp } from '@/utils/date';

import CancelOrganisationInvitationConfirmation from '../CancelOrganisationInvitationConfirmation';

import { Props } from './types';
import { useHandleCopyLink } from './useHandleCopyLink';

const CancelOrganisationInvitation: React.FC<Props> = ({
    avatarURL,
    invitation,
    onClose,
    onCancel,
    handleCancelSuccess,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
        useState(false);
    const { copyLinkButton, linkCopied, containerRef } = useHandleCopyLink(
        invitation.invitation_url,
    );

    const handleCancelPress = () => {
        setIsConfirmationModalOpen(true);
    };

    const handleCloseConfirmationModal = () => {
        setIsConfirmationModalOpen(false);
    };

    const handleConfirmInvitationCancelation = async () => {
        setIsLoading(true);

        await onCancel();

        setIsLoading(false);
        handleCancelSuccess();
    };

    if (isConfirmationModalOpen) {
        return (
            <CancelOrganisationInvitationConfirmation
                onCancel={handleCloseConfirmationModal}
                onConfirm={handleConfirmInvitationCancelation}
                isLoading={isLoading}
            />
        );
    }

    return (
        <Modal isOpen center styles="px-11">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box
                    sx={{ display: 'flex', gap: '14px', alignItems: 'center' }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: '50px',
                            backgroundColor: ENTITY_COLOR.teams,
                        }}
                    >
                        <AccessTimeIcon
                            sx={{
                                width: 22,
                                height: 22,
                                color: COLOR.white,
                            }}
                        />
                    </Box>
                    <Typography
                        color={COLOR.ebonyClay}
                        fontSize={24}
                        fontWeight={700}
                    >
                        {t('TeamsPage:pending_invitation')}
                    </Typography>
                </Box>

                <Box
                    ref={containerRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        position: 'relative',
                    }}
                >
                    <TextField
                        InputProps={{
                            endAdornment: copyLinkButton,
                            style: {
                                height: 48,
                                fontSize: 14,
                                fontWeight: 300,
                                color: '#212B36',
                            },
                            readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        name="invitationLink"
                        sx={{
                            width: 518,
                            borderColor: '#e5e7eb',
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                    borderColor: '#e5e7eb',
                                },
                            },
                        }}
                        focused={false}
                        value={invitation.invitation_url}
                    />

                    <Slide
                        direction="up"
                        in={linkCopied}
                        container={containerRef.current}
                    >
                        <Button
                            variant="text"
                            className="self-center"
                            sx={{
                                width: 150,
                                height: 30,
                                backgroundColor: COLOR.white,
                                boxShadow: '0px 3px 6px #00000029',
                                border: '1px solid #2E46AD33',
                                opacity: 0.86,
                                backdropFilter: 'blur(7px)',
                                WebkitBackdropFilter: 'blur(7px)',
                                '&:hover': {
                                    backgroundColor: COLOR.white,
                                },
                            }}
                            disabled
                            startIcon={
                                <DoneIcon
                                    fontSize="small"
                                    sx={{ color: COLOR.black }}
                                />
                            }
                        >
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 400,
                                    color: COLOR.black,
                                }}
                            >
                                {t(
                                    'TeamInvitationModal:labels:copied_to_clickboard',
                                )}
                            </Typography>
                        </Button>
                    </Slide>
                </Box>

                {invitation.email && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '14px',
                            py: '18px',
                            borderTop: `1px solid ${COLOR.whisper}`,
                            borderBottom: `1px solid ${COLOR.whisper}`,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: '50px',
                                height: '50px',
                                backgroundColor: COLOR.gainsboro,
                            }}
                            src={avatarURL}
                        >
                            <UserProfileIcon color={COLOR.mischka} />
                        </Avatar>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                            }}
                        >
                            <Typography fontSize={16} fontWeight={700}>
                                {invitation.email}
                            </Typography>
                            <Typography
                                fontSize={12}
                                color={addAlphaToColor(COLOR.ebonyClay, 0.4)}
                            >
                                {invitation.contact_id
                                    ? t(
                                          'TeamsPage:cancelModal:invited_via_email',
                                      )
                                    : t(
                                          'TeamsPage:cancelModal:invited_via_email_not_contact',
                                      )}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <DoneAllIcon
                                    sx={{ width: 16, color: COLOR.cozumel }}
                                />
                                <EmailIcon
                                    sx={{ width: 16, color: COLOR.cozumel }}
                                />
                                <Typography
                                    ml="4px"
                                    fontSize={11}
                                    color={addAlphaToColor(
                                        COLOR.ebonyClay,
                                        0.5,
                                    )}
                                >
                                    {t(
                                        'Event:contact_invitations:labels:email_invite_status_sent',
                                    )}
                                </Typography>
                            </Box>

                            <Typography
                                fontSize={10}
                                color={addAlphaToColor(COLOR.ebonyClay, 0.5)}
                            >
                                {formatUnixTimestamp(
                                    invitation.created_at,
                                    'ddd MM-DD-YYYY h:mm a',
                                )}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Typography
                    fontWeight={300}
                    fontSize={14}
                    color={COLOR.ebonyClay}
                >
                    {t('TeamsPage:cancelModal:title')}
                </Typography>

                <Typography
                    fontSize={12}
                    color={addAlphaToColor(COLOR.ebonyClay, 0.4)}
                >
                    {t('TeamsPage:cancelModal:link_info', {
                        date: formatUnixTimestamp(
                            invitation.created_at,
                            'ddd MM-DD-YYYY h:mm a',
                        ),
                    })}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        gap: '8px',
                        alignSelf: 'flex-end',
                        mt: '16px',
                    }}
                >
                    <Button
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: COLOR.tundora,
                            '&:hover': {
                                backgroundColor: COLOR.white,
                            },
                        }}
                        onClick={onClose}
                    >
                        {t('Actions:back')}
                    </Button>
                    <Button
                        sx={{
                            color: COLOR.white,
                            backgroundColor: COLOR.cranberry,
                            fontSize: 12,
                            fontWeight: 700,
                            '&:hover': {
                                backgroundColor: COLOR.cranberry,
                            },
                        }}
                        onClick={handleCancelPress}
                    >
                        {t('TeamsPage:cancelModal:cancel_invitation')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default CancelOrganisationInvitation;
